/**
 * 
    lazy loads img, background-image and iframes
    apply data-lazy-load attribute to img, iframe, or div background element
    set attribute to a value of 'img' | 'iframe' | 'background';

    <img data-lazy-load="img" data-src="{imageurl}" />
    <div data-lazy-load="background" data-src="{imageUrl}"></div>
    <iframe data-lazy-load="iframe" data-src="{iframeUrl}"></iframe>

    Avoid lazy loading while in page editor mode. Add conditional rendering in the views to render the markup normally while in page edit mode.
 */
export const setupLazyLoad = (): void => {
  const lazyLoadingElements: NodeListOf<HTMLElement> = document.querySelectorAll('.lazy-img, .lazy-bg, .lazy-video');
  const loadedClass = 'inView';

  const lazyElementObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry: IntersectionObserverEntry): void => {
      if (entry.isIntersecting && !entry.target.classList.contains(loadedClass)) {
        const element = entry.target as HTMLElement;
        loadElement(element);

        element.classList.add(loadedClass);
        lazyElementObserver.unobserve(element);
      }
    });
  });

  const loadElement = function (element: HTMLElement): void {
    const loadType: string | undefined = element.nodeName.toLowerCase();
    const src: string | undefined = element.dataset.src;
    if (src) {
      switch (loadType) {
        case 'img':
          (element as HTMLImageElement).src = src;
          break;
        case 'iframe':
          (element as HTMLIFrameElement).src = src;
          break;
        case 'div':
          (element as HTMLElement).style.backgroundImage = `url(${src})`;
          break;
        default:
          break;
      }
    }
  };

  lazyLoadingElements.forEach((element: HTMLElement): void => {
    const isPageEditor: boolean =
      document.body.classList.contains('-sf-new-zone-editor') || document.body.classList.contains('sfPageEditor');
    // In case there is no page editor conditional
    // This will just load the images immediately
    if (isPageEditor) {
      loadElement(element);
    } else {
      lazyElementObserver.observe(element);
    }
  });
};
